import React, { useState, useEffect } from 'react';
import Slider from 'react-slick';
import { useNavigate } from 'react-router';
import { productTypes } from '../../../../utils/productHelpers';
import { useUnifiedAnalytics } from '../../../../contexts/AnalyticsWrapper';
import { integerToWord } from '../../../../utils/helpers';
import { NextArrow, PrevArrow } from '../CarouselArrows';
import MaturityNotice, { maturityNoticeVariants } from '../../../MaturityNotice';
import { fisaMaturityLearnMoreUrl, fisaMaturityPageSources } from '../../../MaturityNotice/MaturityNoticeConfig';

/**
 * Single maturing account banner item — extracted outside the parent component
 * to prevent Slick Slider from remounting slides on every render.
 *
 * @param {{ account: any; isMultiple: boolean; onViewOptions: (id: string, code: string) => void }} props
 */

const getMaturityDateKey = (account) => {
    const maturityDate = account.maturity?.maturityDate;
    return maturityDate ? String(maturityDate).split('T')[0] : '';
};

export const groupMaturingAccountsForBanner = (accounts) => {
    const groupedFisaAccounts = new Map();
    const bannerAccounts = [];

    accounts.forEach((account) => {
        const isFisa = account.product.productType === productTypes.FixedRateCashISA;

        if (!isFisa) {
            bannerAccounts.push(account);
            return;
        }

        const groupKey = [
            account.product.name,
            getMaturityDateKey(account)
        ].join('|');

        if (!groupedFisaAccounts.has(groupKey)) {
            const group = {
                ...account,
                accountNumber: account.accountNumber,
                groupedAccountNumbers: [account.accountNumber]
            };

            groupedFisaAccounts.set(groupKey, group);
            bannerAccounts.push(group);
            return;
        }

        const group = groupedFisaAccounts.get(groupKey);
        group.groupedAccountNumbers.push(account.accountNumber);
        group.accountNumber = group.groupedAccountNumbers.join(', ');
    });

    return bannerAccounts;
};

export const filterMaturingAccountsForBanner = (accounts, enableFisaMaturity) => {
    if (enableFisaMaturity) {
        return accounts;
    }

    return accounts.filter((account) => account.product.productType !== productTypes.FixedRateCashISA);
};

const BannerItem = ({ account, isMultiple, onViewOptions }) => (
    <MaturityNotice
        account={account}
        isMultiple={isMultiple}
        variant={maturityNoticeVariants.Dashboard}
        onAction={() => onViewOptions(account.id, account.product.code, account.product.productType)}
        className="new-maturity-banner__notice"
    />
);

/**
 * New Dashboard Maturity Banner - matches Figma Section Message info style
 * (BAU-Inv-Save-2025 node 19181:50778)
 *
 * Behaviour matches old MaturityBanner:
 * - Single account: plain item, no carousel
 * - Multiple accounts desktop (≥1024px): stacked items
 * - Multiple accounts mobile (<1024px): react-slick carousel with arrows
 *
 * @param {{ maturingAccounts: any[] }} props
 */
const NewDashboardMaturityBanner = ({ maturingAccounts }) => {
    const LARGE_BREAKPOINT_WIDTH = 1024;
    const analytics = useUnifiedAnalytics();
    const navigate = useNavigate();
    const [carouselEnabled, setCarouselEnabled] = useState(
        typeof window !== 'undefined' && window.innerWidth < LARGE_BREAKPOINT_WIDTH
    );
    const [currentSlide, setCurrentSlide] = useState(0);

    useEffect(() => {
        const onResize = () => setCarouselEnabled(window.innerWidth < LARGE_BREAKPOINT_WIDTH);
        window.addEventListener('resize', onResize, false);
        return () => window.removeEventListener('resize', onResize, false);
    }, []);

    useEffect(() => {
        setCurrentSlide(0);
    }, [maturingAccounts?.length]);

    const handleViewOptions = (/** @type {string} */ accountId, /** @type {string} */ productCode, /** @type {string} */ productType) => {
        const isFisa = productType === productTypes.FixedRateCashISA;
        const destinationUrl = isFisa ? fisaMaturityLearnMoreUrl : `/maturity-instruction/${accountId}`;

        if (analytics) {
            analytics.trackAnalyticsClickEvent(
                isFisa ? 'learn more' : 'view your options',
                destinationUrl,
                'dashboard maturity banner',
                productCode,
                'maturity',
                {},
                'link click',
                'navigation|view|maturity',
                'maturity'
            );
        }

        navigate(destinationUrl, isFisa ? { state: { source: fisaMaturityPageSources.DashboardNotification } } : undefined);
    };

    if (!maturingAccounts || maturingAccounts.length === 0) {
        return null;
    }

    const bannerAccounts = groupMaturingAccountsForBanner(maturingAccounts);
    const isMultiple = bannerAccounts.length > 1;

    const renderItems = () => {
        if (!isMultiple) {
            return (
                <BannerItem
                    account={bannerAccounts[0]}
                    isMultiple={false}
                    onViewOptions={handleViewOptions}
                />
            );
        }

        // Multiple accounts: carousel on mobile, stacked on desktop — matches old MaturityBanner
        if (carouselEnabled) {
            return (
                <Slider
                    className="accounts__carousel account-carousel"
                    infinite={false}
                    nextArrow={(
                        <NextArrow
                            enabled={currentSlide < bannerAccounts.length - 1}
                            visible={currentSlide < bannerAccounts.length - 1}
                        />
                    )}
                    prevArrow={(
                        <PrevArrow
                            enabled={currentSlide > 0}
                            visible={currentSlide > 0}
                        />
                    )}
                    swipe={true}
                    swipeToSlide={true}
                    draggable={true}
                    touchMove={true}
                    initialSlide={0}
                    beforeChange={(currentIndex, nextIndex) => setCurrentSlide(nextIndex)}
                >
                    {bannerAccounts.map((account) => (
                        <BannerItem
                            key={account.id}
                            account={account}
                            isMultiple={true}
                            onViewOptions={handleViewOptions}
                        />
                    ))}
                </Slider>
            );
        }

        return (
            <>
                {bannerAccounts.map((account) => (
                    <BannerItem
                        key={account.id}
                        account={account}
                        isMultiple={true}
                        onViewOptions={handleViewOptions}
                    />
                ))}
            </>
        );
    };

    return (
        <div className="new-maturity-banner">
            {/* Plural heading when more than one account is maturing — matches old MaturityBanner */}
            {isMultiple && (
                <p className="new-maturity-banner__plural-heading">
                    You have {integerToWord(maturingAccounts.length, true)} accounts maturing soon
                </p>
            )}
            {renderItems()}
        </div>
    );
};

export default NewDashboardMaturityBanner;


